import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import { pageLayoutsQueryOptions, type PageLayout } from "@/lib/layouts.queries";
import {
  createPageLayout,
  deletePageLayout,
  updatePageLayout,
} from "@/lib/layouts.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, LayoutTemplate } from "lucide-react";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { Field } from "@/components/admin/Field";
import { FormDialog, SaveButton, useDirty } from "@/components/admin/FormDialog";

export const Route = createFileRoute("/_authenticated/admin/layouts")({
  component: LayoutsAdminPage,
});

type FormState = {
  id?: string;
  name: string;
  description: string;
  body_html: string;
  display_order: number;
  is_active: boolean;
};

const empty: FormState = {
  name: "",
  description: "",
  body_html: "",
  display_order: 0,
  is_active: true,
};

function LayoutsAdminPage() {
  const { data: rows = [] } = useQuery(pageLayoutsQueryOptions());
  const qc = useQueryClient();
  const create = useServerFn(createPageLayout);
  const update = useServerFn(updatePageLayout);
  const del = useServerFn(deletePageLayout);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const dirty = useDirty(form, open);

  const sorted = useMemo(
    () =>
      [...rows].sort(
        (a, b) => a.display_order - b.display_order || a.name.localeCompare(b.name),
      ),
    [rows],
  );

  const save = useMutation({
    mutationFn: async (f: FormState) => {
      const payload = {
        name: f.name.trim(),
        description: f.description || null,
        body_html: f.body_html,
        display_order: f.display_order,
        is_active: f.is_active,
      };
      if (f.id) return update({ data: { ...payload, id: f.id } });
      return create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["page_layouts"] });
      setOpen(false);
      setForm(empty);
      toast.success("Saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const toggle = useMutation({
    mutationFn: (l: PageLayout) =>
      update({
        data: {
          id: l.id,
          name: l.name,
          description: l.description,
          body_html: l.body_html,
          display_order: l.display_order,
          is_active: !l.is_active,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["page_layouts"] }),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Update failed"),
  });

  const remove = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["page_layouts"] });
      toast.success("Deleted");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  const openEdit = (l: PageLayout) => {
    setForm({
      id: l.id,
      name: l.name,
      description: l.description ?? "",
      body_html: l.body_html ?? "",
      display_order: l.display_order,
      is_active: l.is_active,
    });
    setOpen(true);
  };

  const openNew = () => {
    setForm({ ...empty });
    setOpen(true);
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Page Layouts</CardTitle>
          <CardDescription>
            Reusable HTML blocks editors can drop into a site template body. Style with the
            toolbar — layouts are stored as plain HTML, so they carry their own inline styling.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> New layout
        </Button>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Description</TableHead>
              <TableHead className="w-20 text-center">Order</TableHead>
              <TableHead className="w-20 text-center">Active</TableHead>
              <TableHead className="w-28 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((l) => (
              <TableRow key={l.id} className={l.is_active ? "" : "opacity-60"}>
                <TableCell className="font-medium">{l.name}</TableCell>
                <TableCell className="hidden text-muted-foreground md:table-cell">
                  {l.description || "—"}
                </TableCell>
                <TableCell className="text-center text-muted-foreground">
                  {l.display_order}
                </TableCell>
                <TableCell className="text-center">
                  <Switch checked={l.is_active} onCheckedChange={() => toggle.mutate(l)} />
                </TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" onClick={() => openEdit(l)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm(`Delete layout "${l.name}"?`)) remove.mutate(l.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  No layouts yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      <FormDialog
        open={open}
        onOpenChange={setOpen}
        size="lg"
        icon={LayoutTemplate}
        title={form.id ? "Edit layout" : "New layout"}
        context={form.id ? form.name : "Reusable HTML block for site templates"}
        dirty={dirty}
        busy={save.isPending}
        asForm
        onSubmit={() => {
          if (form.name.trim() && form.body_html.trim()) save.mutate(form);
        }}
        footer={
          <>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <SaveButton
              dirty={dirty}
              pending={save.isPending}
              disabled={!form.name.trim() || !form.body_html.trim()}
            />
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <Field label="Name" required>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
              </Field>
            </div>
            <Field label="Display order">
              <Input
                type="number"
                value={form.display_order}
                onChange={(e) =>
                  setForm({ ...form, display_order: Number(e.target.value) || 0 })
                }
              />
            </Field>
          </div>

          <Field label="Description">
            <Textarea
              rows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="What this layout is for, so editors pick the right one."
            />
          </Field>

          <Field label="Layout body">
            <RichTextEditor
              value={form.body_html}
              onChange={(html) => setForm((f) => ({ ...f, body_html: html }))}
              uploadFolder="layouts"
              minHeight={320}
              templateVariables={[]}
              placeholder="Build the reusable block…"
              allowSource
            />
          </Field>

          <div className="flex items-center gap-3 rounded-md border bg-muted/30 px-3 py-2">
            <Switch
              id="layout-active"
              checked={form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
            />
            <Label htmlFor="layout-active" className="cursor-pointer">Available to editors</Label>
          </div>
        </div>
      </FormDialog>
    </Card>
  );
}
